import { RiAddLine, RiFileCopyLine, RiLoader4Line, RiRefreshLine } from "@remixicon/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

interface OauthClientSummary {
	id: string;
	clientId: string;
	createdAt: string;
	revokedAt: string | null;
}

interface OauthClientCreated {
	id: string;
	clientId: string;
	clientSecret: string;
	tokenUrl: string;
	eventsUrl: string;
}

const LIST_QUERY_KEY = ["farfetch", "oauth-clients"] as const;

function copyToClipboard(value: string) {
	if (navigator.clipboard?.writeText) {
		void navigator.clipboard.writeText(value);
		toast.success("Copied to clipboard");
	}
}

/// One-time reveal panel for a freshly registered/rotated client. Shown once
/// per action - the secret is never retrievable again after this component
/// unmounts (dismissed).
function RevealPanel({
	created,
	onDismiss,
}: {
	created: OauthClientCreated;
	onDismiss: () => void;
}) {
	return (
		<div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4 space-y-3 text-sm">
			<p className="font-medium">Save these now - the secret will not be shown again.</p>
			<div className="space-y-1">
				<Label>Client ID</Label>
				<div className="flex items-center gap-2">
					<code className="flex-1 rounded bg-background px-2 py-1 text-xs break-all">
						{created.clientId}
					</code>
				</div>
			</div>
			<div className="space-y-1">
				<Label>Client Secret</Label>
				<div className="flex items-center gap-2">
					<code className="flex-1 rounded bg-background px-2 py-1 text-xs break-all">
						{created.clientSecret}
					</code>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => copyToClipboard(created.clientSecret)}
					>
						<RiFileCopyLine className="size-4" />
					</Button>
				</div>
			</div>
			<div className="space-y-1">
				<Label>Token URL</Label>
				<code className="block rounded bg-background px-2 py-1 text-xs break-all">
					{created.tokenUrl}
				</code>
			</div>
			<div className="space-y-1">
				<Label>Events URL</Label>
				<code className="block rounded bg-background px-2 py-1 text-xs break-all">
					{created.eventsUrl}
				</code>
			</div>
			<Button type="button" size="sm" onClick={onDismiss}>
				Done
			</Button>
		</div>
	);
}

/// Manage the Farfetch OAuth2 machine client(s) used to authenticate Farfetch's
/// order-events webhook calls back to us. Register hands Farfetch a client_id
/// + secret + the token/events URLs; the secret is shown exactly once. List
/// never shows a secret. Rotate replaces the secret (shown once again);
/// revoke soft-deletes the client.
export function FarfetchOauthClients() {
	const qc = useQueryClient();
	const [revealed, setRevealed] = useState<OauthClientCreated | null>(null);
	const [revokeTarget, setRevokeTarget] = useState<OauthClientSummary | null>(null);

	const query = useQuery({
		queryKey: LIST_QUERY_KEY,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/api/v1/farfetch/oauth-clients" as never);
			if (error) throw new Error("failed to load OAuth clients");
			return data as OauthClientSummary[];
		},
	});

	const create = useMutation({
		mutationFn: async () => {
			const { data, error } = await fetchClient.POST("/api/v1/farfetch/oauth-clients" as never);
			if (error) throw new Error("failed to register client");
			return data as OauthClientCreated;
		},
		onSuccess: (created) => {
			setRevealed(created);
			qc.invalidateQueries({ queryKey: LIST_QUERY_KEY });
			toast.success("Client registered");
		},
		onError: () => toast.error("Failed to register client"),
	});

	const rotate = useMutation({
		mutationFn: async (id: string) => {
			const { data, error } = await fetchClient.POST(
				"/api/v1/farfetch/oauth-clients/{id}/rotate" as never,
				{ params: { path: { id } } } as never,
			);
			if (error) throw new Error("failed to rotate secret");
			return data as OauthClientCreated;
		},
		onSuccess: (created) => {
			setRevealed(created);
			qc.invalidateQueries({ queryKey: LIST_QUERY_KEY });
			toast.success("Secret rotated");
		},
		onError: () => toast.error("Failed to rotate secret"),
	});

	const revoke = useMutation({
		mutationFn: async (id: string) => {
			const { error } = await fetchClient.DELETE(
				"/api/v1/farfetch/oauth-clients/{id}" as never,
				{ params: { path: { id } } } as never,
			);
			if (error) throw new Error("failed to revoke client");
		},
		onSuccess: () => {
			setRevokeTarget(null);
			qc.invalidateQueries({ queryKey: LIST_QUERY_KEY });
			toast.success("Client revoked");
		},
		onError: () => toast.error("Failed to revoke client"),
	});

	if (query.isLoading) {
		return (
			<div className="space-y-2">
				{[0, 1].map((i) => (
					<Skeleton key={i} className="h-9 w-full" />
				))}
			</div>
		);
	}

	if (query.isError) {
		return (
			<div
				role="alert"
				className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-2"
			>
				<p className="font-medium">Failed to load OAuth clients.</p>
				<Button type="button" variant="outline" size="sm" onClick={() => query.refetch()}>
					Retry
				</Button>
			</div>
		);
	}

	const clients = query.data ?? [];

	return (
		<div className="space-y-3">
			{revealed && <RevealPanel created={revealed} onDismiss={() => setRevealed(null)} />}

			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">
					Machine credentials Farfetch uses to authenticate order-events webhook calls to us.
				</p>
				<Button type="button" size="sm" disabled={create.isPending} onClick={() => create.mutate()}>
					{create.isPending ? (
						<RiLoader4Line className="mr-1 size-4 animate-spin" />
					) : (
						<RiAddLine className="mr-1 size-4" />
					)}
					Register client
				</Button>
			</div>

			{clients.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					No OAuth clients registered yet - register one to hand Farfetch the credentials.
				</p>
			) : (
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Client ID</TableHead>
								<TableHead>Created</TableHead>
								<TableHead>Status</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{clients.map((c) => (
								<TableRow key={c.id}>
									<TableCell className="font-mono text-xs">{c.clientId}</TableCell>
									<TableCell className="text-xs text-muted-foreground">
										{new Date(c.createdAt).toLocaleString()}
									</TableCell>
									<TableCell>
										{c.revokedAt ? (
											<Badge variant="destructive">Revoked</Badge>
										) : (
											<Badge variant="secondary">Active</Badge>
										)}
									</TableCell>
									<TableCell className="text-right">
										{!c.revokedAt && (
											<div className="flex items-center justify-end gap-1">
												<Button
													type="button"
													variant="outline"
													size="sm"
													disabled={rotate.isPending}
													onClick={() => rotate.mutate(c.id)}
												>
													{rotate.isPending ? (
														<RiLoader4Line className="mr-1 size-4 animate-spin" />
													) : (
														<RiRefreshLine className="mr-1 size-4" />
													)}
													Rotate
												</Button>
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => setRevokeTarget(c)}
												>
													Revoke
												</Button>
											</div>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			<AlertDialog
				open={revokeTarget !== null}
				onOpenChange={(open) => !open && setRevokeTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Revoke this client?</AlertDialogTitle>
						<AlertDialogDescription>
							Farfetch will no longer be able to authenticate order-events calls using{" "}
							<code className="text-xs">{revokeTarget?.clientId}</code>. This cannot be undone -
							register a new client if Farfetch needs continued access.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (revokeTarget) revoke.mutate(revokeTarget.id);
							}}
						>
							Revoke
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
